/**
 * A bounded description of a boundary-caught error, built from an allowlist
 * of message shapes whose variable parts are code identifiers, never content.
 */
export type BoundaryErrorClassification = {
  error_class: string;
  error_detail?: Record<string, string | number>;
};

// Component names and the tiptap view property come from source code, so
// anything that does not look like an identifier is dropped rather than reported.
const COMPONENT_NAME = /^[A-Z][A-Za-z0-9.]{0,63}$/;
const PROPERTY_NAME = /^[A-Za-z_$][\w$]{0,39}$/;

const pick = (value: string | undefined, shape: RegExp): string | undefined =>
  value !== undefined && shape.test(value) ? value : undefined;

const withDetail = (
  errorClass: string,
  detail: Record<string, string | number | undefined>
): BoundaryErrorClassification => {
  const kept = Object.fromEntries(
    Object.entries(detail).filter(
      (entry): entry is [string, string | number] => entry[1] !== undefined
    )
  );
  return Object.keys(kept).length > 0
    ? { error_class: errorClass, error_detail: kept }
    : { error_class: errorClass };
};

type Rule = (message: string, name: string) => BoundaryErrorClassification | undefined;

const RULES: readonly Rule[] = [
  // Radix: "`Consumer` must be used within `Provider`".
  (message) => {
    const match = /^`([^`]*)` must be used within `([^`]*)`$/.exec(message);
    if (!match) return undefined;
    return withDetail('react_context_missing', {
      consumer: pick(match[1], COMPONENT_NAME),
      provider: pick(match[2], COMPONENT_NAME),
    });
  },
  // Our own context hooks: "useThing must be used within a ThingProvider".
  (message) => {
    const match =
      /^(use[A-Z][A-Za-z0-9]{0,63}) must be used within (?:an? )?<?([A-Z][A-Za-z0-9]{0,63})>?\.?$/.exec(
        message
      );
    if (!match) return undefined;
    return withDetail('react_context_missing', {
      consumer: match[1],
      provider: match[2],
    });
  },
  // Production React replaces its messages with a code; the args can carry
  // rendered values, so only the code is kept.
  (message) => {
    const match = /Minified React error #(\d{1,4})\b/.exec(message);
    if (!match) return undefined;
    return withDetail('react_minified', { react_error_code: Number(match[1]) });
  },
  (message) => {
    if (!message.includes('The editor view is not available')) return undefined;
    const match = /Cannot access view\['([^']*)'\]/.exec(message);
    return withDetail('tiptap_view_unavailable', {
      view_property: pick(match?.[1], PROPERTY_NAME),
    });
  },
  // The key named in a nullish read can be a runtime value (`map[name]`), so
  // only whether the base was undefined or null is kept.
  (message, name) => {
    if (name !== 'TypeError') return undefined;
    const chromium =
      /^Cannot read properties of (undefined|null) \(reading '[^']*'\)$/.exec(message);
    if (chromium) {
      return withDetail('property_of_nullish', { nullish: chromium[1] });
    }
    const firefox = /^can't access property "[^"]*", .* is (undefined|null)$/.exec(
      message
    );
    if (firefox) {
      return withDetail('property_of_nullish', { nullish: firefox[1] });
    }
    // Safari quotes the whole expression, so only the class is kept.
    if (/^(undefined|null) is not an object \(evaluating /.test(message)) {
      return { error_class: 'property_of_nullish' };
    }
    if (/ is not a function\b/.test(message)) return { error_class: 'not_a_function' };
    return undefined;
  },
  (message, name) => {
    if (name !== 'RangeError') return undefined;
    if (/^Position -?\d+ (out of range|outside of fragment)\b/.test(message)) {
      return { error_class: 'pm_position_out_of_range' };
    }
    if (/^Index -?\d+ out of range\b/.test(message)) {
      return { error_class: 'pm_index_out_of_range' };
    }
    if (message === 'Maximum call stack size exceeded') {
      return { error_class: 'stack_overflow' };
    }
    if (/^No cell with offset -?\d+ found$/.test(message)) {
      return { error_class: 'pm_table_cell_missing' };
    }
    if (/^Invalid content for node\b/.test(message)) {
      return { error_class: 'pm_invalid_content' };
    }
    return undefined;
  },
  (message, name) =>
    name === 'SyntaxError' && /JSON/.test(message)
      ? { error_class: 'json_parse' }
      : undefined,
];

export const classifyBoundaryError = (
  error: unknown
): BoundaryErrorClassification | undefined => {
  if (!(error instanceof Error) || typeof error.message !== 'string') return undefined;
  for (const rule of RULES) {
    const classification = rule(error.message, error.name);
    if (classification) return classification;
  }
  return undefined;
};
